import React, { useState, useEffect } from 'react';
import DashboardCard from '../components/ui/DashboardCard/DashboardCard';
import Button from '../components/ui/Button/Button';
import StatusBadge from '../components/ui/StatusBadge/StatusBadge';
import InvoiceCard from '../components/InvoiceCard/InvoiceCard';
import InvoiceModal from '../components/InvoiceModal/InvoiceModal';
import styles from './Dashboard.module.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import type { Invoice } from '../types';
import { supabase } from '../supabaseClient';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast, { Toaster } from 'react-hot-toast';
import { io, Socket } from 'socket.io-client';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for UI interactions
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [selectedInvoiceForModal, setSelectedInvoiceForModal] = useState<Invoice | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Phase 3: Real-time sync progress state
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'complete' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [socket, setSocket] = useState<Socket | null>(null);
  // Debugging states for Socket.IO connection
  const [isConnected, setIsConnected] = useState(false);
  const [eventsReceivedCount, setEventsReceivedCount] = useState(0);
  const [recentActivity] = useState([
    {
      id: 1,
      type: 'info',
      description: 'Invoice #INV001 reminder sent to John Doe.',
      timestamp: '2 hours ago',
    },
    {
      id: 2,
      type: 'success',
      description: 'Invoice #INV005 marked as paid by Jane Smith.',
      timestamp: 'Yesterday',
    },
    {
      id: 3,
      type: 'warning',
      description: 'Automation paused for Customer ABC.',
      timestamp: '3 days ago',
    },
    {
      id: 4,
      type: 'info',
      description: 'Dashboard data refreshed.',
      timestamp: new Date().toLocaleTimeString(),
    },
  ]);

  const API_BASE_URL = import.meta.env.VITE_APP_BACKEND_URL || 'http://localhost:5000';

  console.log('Dashboard.tsx: Initial render - API_BASE_URL:', API_BASE_URL);

  // Set Axios default base URL
  useEffect(() => {
    axios.defaults.baseURL = API_BASE_URL;
    console.log('Dashboard.tsx: Axios default baseURL set to:', axios.defaults.baseURL);

    // Add Axios request interceptor for detailed logging
    console.log('Dashboard.tsx: useEffect - setting up Axios interceptor.');
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        console.log('[Axios Interceptor] Request Sent:');
        console.log('  Method:', config.method?.toUpperCase());
        console.log('  URL:', config.url);
        console.log('  Headers:', config.headers);
        console.log('  Data:', config.data);
        console.log('  With Credentials:', config.withCredentials);
        return config;
      },
      (error) => {
        console.error('[Axios Interceptor] Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on component unmount
    return () => {
      console.log('Dashboard.tsx: Cleaning up Axios interceptor.');
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [API_BASE_URL]); // Re-run if API_BASE_URL changes

  // Helper function to fetch dashboard data
  const fetchDashboardData = async (userId: string) => {
    console.log('Dashboard.tsx: fetchDashboardData called for user ID:', userId);
    // Use relative URL as baseURL is now configured
    const requestUrl = `/api/dashboard/invoices?user_id=${userId}&status=overdue`;
    console.log('[Frontend] Sending GET request to:', requestUrl);
    const response = await axios.get<Invoice[]>(requestUrl, { withCredentials: true });
    console.log('Dashboard.tsx: Backend API invoices fetch result - data:', response.data);
    return response.data || [];
  };

  // Dashboard data query
  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ['dashboard-data'],
    queryFn: async () => {
      console.log('Dashboard.tsx: useQuery - Attempting to get user session from Supabase.');
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      console.log('Dashboard.tsx: supabase.auth.getUser result - user:', user, 'userError:', userError);

      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }
      return fetchDashboardData(user.id);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Handle query errors with useEffect
  useEffect(() => {
    if (error) {
      console.error('Dashboard.tsx: Error fetching dashboard data with React Query:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      toast.error(`Failed to load dashboard data: ${errorMessage}.`);
    }
  }, [error]);

  // QuickBooks sync mutation
  const { mutate: triggerSync, isPending: isSyncing } = useMutation({
    mutationFn: async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }
      const response = await fetch(`${API_BASE_URL}/api/quickbooks/sync?user_id=${user.id}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Sync failed');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      toast.success('QuickBooks data synced successfully! 🎉');
    },
    onError: (err: Error) => {
      console.error('QuickBooks sync failed:', err);
      toast.error(`Sync failed: ${err.message || 'Please try again.'}`);
    }
  });

  // Phase 3: Socket.IO setup for real-time updates
  useEffect(() => {
    const setupSocket = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) return;

        const socketConnection = io(API_BASE_URL);
        setSocket(socketConnection);
        console.log('Socket.IO client initialized with URL:', API_BASE_URL);

        // Listen for connect event
        socketConnection.on('connect', () => {
          console.log('Socket.IO client connected to server.');
          setIsConnected(true); // Update connection status
        });

        // Listen for disconnect event
        socketConnection.on('disconnect', (reason) => {
          console.log('Socket.IO client disconnected from server. Reason:', reason);
          setIsConnected(false); // Update connection status
        });

        // Join user-specific room
        console.log('Emitting join-user-room for:', `user-${user.id}`);
        socketConnection.emit('join-user-room', `user-${user.id}`);

        // Listen for connection confirmation
        socketConnection.on('connection-confirmed', (data) => {
          console.log('Socket.IO connection confirmed event received:', data);
        });

        // Listen for sync progress updates
        socketConnection.on('sync-progress', (data) => {
          console.log('Received sync-progress event:', data);
          setSyncProgress(data.progress);
          setSyncStatus('syncing');
          setSyncMessage(data.message);
          setEventsReceivedCount(prevCount => prevCount + 1); // Increment event count
        });

        // Listen for sync completion
        socketConnection.on('sync-complete', (data) => {
          console.log('Received sync-complete event:', data);
          setSyncProgress(100);
          setSyncStatus('complete');
          setSyncMessage(data.message);
          toast.success(data.message);
          queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
          setEventsReceivedCount(prevCount => prevCount + 1); // Increment event count
          
          // Reset status after a delay
          setTimeout(() => {
            setSyncStatus('idle');
            setSyncProgress(0);
            setSyncMessage('');
            setEventsReceivedCount(0); // Reset event count on completion
          }, 3000);
        });

        // Listen for sync errors
        socketConnection.on('sync-error', (data) => {
          console.error('Received sync-error event:', data);
          setSyncProgress(0);
          setSyncStatus('error');
          setSyncMessage(data.message);
          toast.error(data.message);
          setEventsReceivedCount(prevCount => prevCount + 1); // Increment event count
          
          // Reset status after a delay
          setTimeout(() => {
            setSyncStatus('idle');
            setSyncMessage('');
            setEventsReceivedCount(0); // Reset event count on error
          }, 5000);
        });

        // Listen for sync start
        socketConnection.on('sync-started', (data) => {
          console.log('Received sync-started event:', data);
          setSyncProgress(0);
          setSyncStatus('syncing');
          setSyncMessage(data.message);
          setEventsReceivedCount(prevCount => prevCount + 1); // Increment event count
        });

        return () => {
          socketConnection.disconnect();
        };
      } catch (error) {
        console.error('Socket setup error:', error);
      }
    };

    setupSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [API_BASE_URL, queryClient]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const quickbooksStatus = urlParams.get('quickbooks');
    
    if (quickbooksStatus === 'success') {
      toast.success('QuickBooks connected successfully! Click "Sync QuickBooks" to import your data.');
      window.history.replaceState({}, '', '/dashboard');
    } else if (quickbooksStatus === 'error') {
      toast.error('QuickBooks connection failed. Please try again.');
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  const handleBatchSendReminders = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select invoices to send reminders.');
      return;
    }
    if (!window.confirm(`Are you sure you want to send reminders for ${selectedInvoices.length} selected invoices?`)) {
      return;
    }

    setSendingReminder('batch');
    try {
      const invoicesToSend = invoices.filter(inv => selectedInvoices.includes(inv.id));
      for (const invoice of invoicesToSend) {
        await handleSendReminder(invoice);
      }
      toast.success('Batch reminders sent successfully!');
      setSelectedInvoices([]);
    } catch (err: any) {
      console.error('Error sending batch reminders:', err);
      toast.error(`Failed to send batch reminders: ${err.message || 'Network error'}. Check console for details.`);
    } finally {
      setSendingReminder(null);
    }
  };

  const handleBatchMarkAsPaid = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select invoices to mark as paid.');
      return;
    }
    if (!window.confirm(`Are you sure you want to mark ${selectedInvoices.length} selected invoices as paid?`)) {
      return;
    }

    try {
      for (const invoiceId of selectedInvoices) {
        await handleMarkAsPaid(invoiceId);
      }
      toast.success('Batch invoices marked as paid successfully!');
      setSelectedInvoices([]);
    } catch (err: any) {
      console.error('Error marking batch invoices as paid:', err);
      toast.error(`Failed to mark batch invoices as paid: ${err.message || 'Network error'}. Check console for details.`);
    }
  };

  const handleToggleAutomation = async (customerId: string, currentStatus: boolean, customerName: string) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }

      const newStatus = !currentStatus;

      console.log('[Frontend] API Base URL:', API_BASE_URL);
      console.log(`[Frontend] Attempting to toggle automation for customer ${customerId} to ${newStatus}.`);
      const requestUrl = `${API_BASE_URL}/customers/${customerId}/toggle-automation`;
      console.log('[Frontend] Sending PATCH request to:', requestUrl);
      console.log('[Frontend] Request Body:', { automationPaused: newStatus, userId: user.id });
      console.log('[Frontend] With Credentials:', true);

      const response = await axios.patch(requestUrl, {
        automationPaused: newStatus,
        userId: user.id,
      }, { withCredentials: true });

      if (response.status === 200) {
        toast.success(`Automation ${newStatus ? 'paused' : 'resumed'} for ${customerName}.`);
        queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      } else {
        console.error('Failed to toggle automation. Full response:', response);
        throw new Error(response.data.message || `Failed to toggle automation with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error toggling automation:', err.response?.data || err.message || err);
      toast.error(`Failed to toggle automation: ${err.response?.data?.message || err.message || 'Network error'}. Check console for details.`);
    }
  };

  const calculateMetrics = () => {
    const now = new Date();
    let totalOverdueAmount = 0;
    let overdueCount = 0;
    let inFollowUpCount = 0;
    let paidThisWeekAmount = 0;
    let paidThisWeekCount = 0;
    let actionNeededCount = 0;

    invoices.forEach(invoice => {
      const dueDate = new Date(invoice.due_date);
      const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

      if (invoice.status === 'Overdue') {
        totalOverdueAmount += invoice.amount;
        overdueCount++;
        if (daysOverdue > 0 && daysOverdue <= 60) {
          inFollowUpCount++;
        }
      } else if (invoice.status === 'Paid') {
        const updatedAt = new Date(invoice.updated_at || invoice.created_at);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (updatedAt.getTime() >= sevenDaysAgo.getTime()) {
          paidThisWeekAmount += invoice.amount;
          paidThisWeekCount++;
        }
      }
      if (daysOverdue > 60 && invoice.status === 'Overdue') {
        actionNeededCount++;
      }
    });

    return [
      {
        title: 'Total Overdue',
        value: `$${totalOverdueAmount.toFixed(2)}`,
        subtitle: `${overdueCount} invoices • Average ${overdueCount > 0 ? Math.floor(totalOverdueAmount / overdueCount) : 0} days overdue`,
        variant: 'danger',
      },
      {
        title: 'Paid This Week',
        value: `$${paidThisWeekAmount.toFixed(2)}`,
        subtitle: `${paidThisWeekCount} invoices • Last payment received recently`,
        variant: 'success',
      },
      {
        title: 'In Follow-up',
        value: `${inFollowUpCount} Invoices`,
        subtitle: 'Next reminder in 2 days (placeholder)',
        variant: 'warning',
      },
      {
        title: 'Action Needed',
        value: `${actionNeededCount} Invoices`,
        subtitle: 'Requires manual review (e.g., very old overdue)',
        variant: 'warning',
      },
    ].map(metric => {
      let color: 'green' | 'red' | 'amber' | 'blue' = 'blue';
      if (metric.variant === 'danger') {
        color = 'red';
      } else if (metric.variant === 'success') {
        color = 'green';
      } else if (metric.variant === 'warning') {
        color = 'amber';
      }
      return {
        ...metric,
        value: <span className="font-mono">{metric.value}</span>,
        color: color,
      };
    });
  };

  const dashboardMetrics = calculateMetrics();

  const handleSendReminder = async (invoice: Invoice) => {
    setSendingReminder(invoice.id);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }

      const recipientEmail = invoice.customers?.email;
      const customerName = invoice.customers?.name || invoice.customer_name;

      console.log('[Frontend] API Base URL:', API_BASE_URL);
      console.log('Attempting to send reminder for invoice:', invoice.invoice_number);
      console.log('Recipient Email:', recipientEmail);
      console.log('Customer Name:', customerName);

      if (!recipientEmail) {
        throw new Error('Customer email is missing for this invoice. Cannot send reminder.');
      }

      const subject = `Reminder: Invoice ${invoice.invoice_number} for ${invoice.currency} ${invoice.amount.toFixed(2)} is Overdue`;
      const body = `
        <p>Dear ${customerName},</p>
        <p>This is a friendly reminder that Invoice ${invoice.invoice_number} for ${invoice.currency} ${invoice.amount.toFixed(2)} was due on ${invoice.due_date}.</p>
        <p>Please make your payment at your earliest convenience. You can pay here: <a href="${invoice.payment_link || '#'}">Pay Now</a></p>
        <p>Thank you for your prompt attention to this matter.</p>
        <p>Sincerely,<br>Your Business Name</p>
      `;

      const requestUrl = `${API_BASE_URL}/send-reminder`;
      console.log('[Frontend] Sending POST request to:', requestUrl);
      const response = await axios.post(requestUrl, {
        userId: user.id,
        invoiceId: invoice.id,
        recipientEmail,
        subject,
        body,
      }, { withCredentials: true });

      if (response.status === 200) {
        toast.success('Reminder sent successfully!');
        queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      } else {
        console.error('Failed to send reminder. Full response:', response);
        throw new Error(response.data.message || `Failed to send reminder with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error sending reminder:', err.response?.data || err.message || err);
      toast.error(`Failed to send reminder: ${err.response?.data?.message || err.message || 'Network error'}. Check console for details.`);
    } finally {
      setSendingReminder(null);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }

      console.log('[Frontend] API Base URL:', API_BASE_URL);
      console.log(`[Frontend] Attempting to mark invoice ${invoiceId} as paid.`);
      const requestUrl = `${API_BASE_URL}/invoices/${invoiceId}/mark-paid`;
      console.log('[Frontend] Sending POST request to:', requestUrl);
      console.log('[Frontend] Request Body:', { userId: user.id });
      console.log('[Frontend] With Credentials:', true);

      const response = await axios.post(requestUrl, {
        userId: user.id,
      }, { withCredentials: true });

      if (response.status === 200) {
        console.log('[Frontend] Invoice marked as paid successfully!');
        toast.success('Invoice marked as paid successfully!');
        queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
      } else {
        console.error('Failed to mark invoice as paid. Full response:', response);
        throw new Error(response.data.message || `Failed to mark invoice as paid with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error marking invoice as paid:', err.response?.data || err.message || err);
      toast.error(`Failed to mark invoice as paid: ${err.response?.data?.message || err.message || 'Network error'}. Check console for details.`);
    }
  };

  const getDaysOverdue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = Math.abs(now.getTime() - due.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return due.getTime() < now.getTime() ? diffDays : 0;
  };

  const getInvoiceStatus = (invoice: Invoice) => {
    if (invoice.status === 'Paid') return 'Paid';
    const daysOverdue = getDaysOverdue(invoice.due_date);
    if (daysOverdue > 0) return 'Overdue';
    return 'Pending';
  };

  const getFollowUpStage = (invoice: Invoice) => {
    if (invoice.status === 'Paid') return 'N/A (Paid)';
    if (invoice.customers?.automation_paused) return 'Automation Paused';

    const daysOverdue = getDaysOverdue(invoice.due_date);
    const reminderCount = invoice.reminder_count || 0;

    if (daysOverdue <= 0) return 'Not Due Yet';

    if (daysOverdue >= 50 && reminderCount >= 4) return 'Final Notice Sent';
    if (daysOverdue >= 35 && reminderCount >= 3) return 'Urgent Reminder Sent';
    if (daysOverdue >= 21 && reminderCount >= 2) return 'Professional Follow-up Sent';
    if (daysOverdue >= 7 && reminderCount >= 1) return 'Friendly Reminder Sent';

    if (daysOverdue >= 50) return 'Due for Final Notice';
    if (daysOverdue >= 35) return 'Due for Urgent Reminder';
    if (daysOverdue >= 21) return 'Due for Professional Follow-up';
    if (daysOverdue >= 7) return 'Due for Friendly Reminder';

    return 'Overdue (No Reminder Sent)';
  };

  const sortedInvoices = React.useMemo(() => {
    let sortableInvoices = [...invoices];

    if (sortConfig !== null) {
      sortableInvoices.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'invoice_number':
          case 'customer_name':
            aValue = a[sortConfig.key].toLowerCase();
            bValue = b[sortConfig.key].toLowerCase();
            break;
          case 'amount':
            aValue = a.amount;
            bValue = b.amount;
            break;
          case 'due_date':
            aValue = new Date(a.due_date).getTime();
            bValue = new Date(b.due_date).getTime();
            break;
          case 'days_overdue':
            aValue = getDaysOverdue(a.due_date);
            bValue = getDaysOverdue(b.due_date);
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          default:
            aValue = a[sortConfig.key as keyof Invoice];
            bValue = b[sortConfig.key as keyof Invoice];
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    } else {
      sortableInvoices.sort((a, b) => getDaysOverdue(b.due_date) - getDaysOverdue(a.due_date));
    }
    return sortableInvoices;
  }, [invoices, sortConfig]);

  const requestSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'ascending' ? ' 🔼' : ' 🔽';
  };

  const handleSelectInvoice = (invoiceId: string) => {
    setSelectedInvoices((prevSelected) =>
      prevSelected.includes(invoiceId)
        ? prevSelected.filter((id) => id !== invoiceId)
        : [...prevSelected, invoiceId]
    );
  };

  const handleSelectAllInvoices = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allInvoiceIds = sortedInvoices.map((invoice) => invoice.id);
      setSelectedInvoices(allInvoiceIds);
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleViewInvoice = async (invoice: Invoice) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, customers(id, name, email, phone, address, payment_terms, is_vip, automation_paused, quickbooks_customer_id, original_email)')
        .eq('id', invoice.id)
        .single();

      if (error) {
        console.error('Error fetching complete invoice data:', error);
        setSelectedInvoiceForModal(invoice);
      } else {
        setSelectedInvoiceForModal(data);
      }
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      setSelectedInvoiceForModal(invoice);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInvoiceForModal(null);
  };

  const handleQuickBooksConnect = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Authentication required. Please log in.');
      }
      const requestUrl = `${API_BASE_URL}/api/quickbooks/connect?user_id=${user.id}`;
      window.location.href = requestUrl;
    } catch (err: any) {
      toast.error(`Failed to connect to QuickBooks: ${err.message}`);
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      <Toaster position="top-right" />
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.pageTitle}>Invoice Chaser Dashboard</h1>
          <div className={styles.headerRight}>
            <span className={styles.businessName}>[Business Name Placeholder]</span>
            <span className={styles.notificationIcon}>🔔</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button 
            variant="primary" 
            onClick={handleQuickBooksConnect}
          >
            Connect to QuickBooks
          </Button>
          <Button 
            variant="primary" 
            onClick={() => triggerSync()}
            disabled={isSyncing}
          >
            {isSyncing ? 'Syncing...' : 'Sync QuickBooks'}
          </Button>
          <Button variant="secondary" onClick={async () => {
            await supabase.auth.signOut();
            navigate('/');
          }}>
            Logout
          </Button>
        </div>
      </div>

      {error && <div className={styles.errorMessage}>{(error as Error).message}</div>}

      {/* Phase 3: Real-time Sync Progress Bar */}
      {syncStatus === 'syncing' && (
        <section className={styles.section}>
          <div className={styles.progressContainer}>
            <div className={styles.progressHeader}>
              <h3>QuickBooks Sync in Progress</h3>
              <span>{syncProgress}% Complete</span>
            </div>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${syncProgress}%` }}
              />
            </div>
            <p className={styles.progressMessage}>{syncMessage}</p>
          </div>
        </section>
      )}

      <section className={`${styles.section} ${styles.metricsGrid}`}>
        {isLoading ? (
          <p>Loading metrics...</p>
        ) : (
          dashboardMetrics.map((metric, index) => (
            <DashboardCard
              key={index}
              title={metric.title}
              value={metric.value}
              subtitle={metric.subtitle}
              color={metric.color}
            />
          ))
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Overdue Invoices</h2>
        {isLoading ? (
          <div>Loading invoices...</div>
        ) : error ? (
          <div>Error loading invoices: {(error as Error).message}</div>
        ) : (
          <>
            {selectedInvoices.length > 0 && (
              <div className={styles.batchActionsContainer}>
                <Button variant="primary" onClick={handleBatchSendReminders}>
                  Send Reminders ({selectedInvoices.length})
                </Button>
                <Button variant="success" onClick={handleBatchMarkAsPaid}>
                  Mark as Paid ({selectedInvoices.length})
                </Button>
              </div>
            )}

            <div className={`show-on-mobile ${styles.mobileInvoiceCards}`}>
              {sortedInvoices.length === 0 ? (
                <p className={styles.noDataMessage}>No overdue invoices found. Great job!</p>
              ) : (
                <div className="mobile-card-grid">
                  {sortedInvoices.map((invoice) => (
                    <InvoiceCard
                      key={invoice.id}
                      invoice={invoice}
                      onSendReminder={handleSendReminder}
                      onMarkAsPaid={handleMarkAsPaid}
                      onToggleAutomation={handleToggleAutomation}
                      isAutomationPaused={invoice.customers?.automation_paused || false}
                      onSelect={handleSelectInvoice}
                      isSelected={selectedInvoices.includes(invoice.id)}
                      getFollowUpStage={getFollowUpStage}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className={`${styles.invoiceTableContainer} hide-on-mobile`}>
              <table className={styles.invoiceTable}>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllInvoices}
                        checked={selectedInvoices.length === sortedInvoices.length && sortedInvoices.length > 0}
                        disabled={sortedInvoices.length === 0}
                      />
                    </th>
                    <th onClick={() => requestSort('invoice_number')}>Invoice ID{getSortIndicator('invoice_number')}</th>
                    <th onClick={() => requestSort('customer_name')}>Customer{getSortIndicator('customer_name')}</th>
                    <th className="text-right" onClick={() => requestSort('amount')}>Amount{getSortIndicator('amount')}</th>
                    <th onClick={() => requestSort('due_date')}>Due Date{getSortIndicator('due_date')}</th>
                    <th onClick={() => requestSort('days_overdue')}>Days Overdue{getSortIndicator('days_overdue')}</th>
                    <th onClick={() => requestSort('status')}>Status{getSortIndicator('status')}</th>
                    <th>Follow-up Stage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={styles.noDataMessage}>No overdue invoices found. Great job!</td>
                    </tr>
                  ) : (
                    sortedInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedInvoices.includes(invoice.id)}
                            onChange={() => handleSelectInvoice(invoice.id)}
                          />
                        </td>
                        <td data-label="Invoice ID">{invoice.invoice_number}</td>
                        <td data-label="Customer">{invoice.customer_name}</td>
                        <td data-label="Amount" className="font-mono text-right">{invoice.currency} {invoice.amount.toFixed(2)}</td>
                        <td data-label="Due Date">{new Date(invoice.due_date).toLocaleDateString()}</td>
                        <td data-label="Days Overdue">{getDaysOverdue(invoice.due_date)}</td>
                        <td data-label="Status">
                          <StatusBadge status={getInvoiceStatus(invoice)} />
                        </td>
                        <td data-label="Follow-up Stage">{getFollowUpStage(invoice)}</td>
                        <td data-label="Actions" className={styles.actionsCell}>
                          <Button
                            variant="primary"
                            onClick={() => {
                              console.log('Send Reminder button clicked for invoice:', invoice.invoice_number);
                              handleSendReminder(invoice);
                            }}
                            disabled={sendingReminder === invoice.id}
                          >
                            {sendingReminder === invoice.id ? 'Sending...' : 'Send Reminder'}
                          </Button>
                          <Button
                            variant="success"
                            onClick={() => handleMarkAsPaid(invoice.id)}
                            disabled={invoice.status === 'Paid'}
                          >
                            Mark as Paid
                          </Button>
                          {invoice.customers && (
                            <Button
                              variant={invoice.customers.automation_paused ? 'warning' : 'secondary'}
                              onClick={() => handleToggleAutomation(
                                invoice.customer_id,
                                invoice.customers!.automation_paused,
                                invoice.customers!.name || invoice.customer_name
                              )}
                            >
                              {invoice.customers.automation_paused ? 'Resume Automation' : 'Pause Automation'}
                            </Button>
                          )}
                          <Button variant="secondary" onClick={() => handleViewInvoice(invoice)}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* Recent Activity Feed */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Recent Activity</h2>
        <div className={styles.activityFeed}>
          {recentActivity.map((activity) => (
            <div key={activity.id} className={styles.activityItem}>
              <span className={styles.activityDescription}>{activity.description}</span>
              <span className={styles.activityTimestamp}>{activity.timestamp}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Invoice Modal */}
      {selectedInvoiceForModal && (
        <InvoiceModal
          invoice={selectedInvoiceForModal}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSendReminder={handleSendReminder}
          onMarkAsPaid={handleMarkAsPaid}
          onToggleAutomation={handleToggleAutomation}
        />
      )}

      {/* Debugging UI for Socket.IO */}
      <div style={{ position: 'fixed', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '5px', borderRadius: '5px', fontSize: '12px', zIndex: 1000 }}>
        <p>Socket Connected: {isConnected ? 'Yes' : 'No'}</p>
        <p>Sync Events Received: {eventsReceivedCount}</p>
      </div>
    </div>
  );
};

export default Dashboard;
